import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  User, 
  Package, 
  MapPin, 
  LogOut, 
  Edit2, 
  Save,
  ArrowLeft,
  ShoppingBag,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout, updateUser, checkAuth } = useAuth();
  
  const [user, setUser] = useState(location.state?.user || authUser);
  const [orders, setOrders] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: ""
  });
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      updateUser(location.state.user);
      setLoading(false);
      fetchOrders();
    } else if (!authUser) {
      checkAuth().then(() => {
        if (!authUser) {
          navigate("/", { replace: true });
        }
      });
    } else {
      setUser(authUser);
      setLoading(false);
      fetchOrders();
    }
  }, [authUser, location.state, navigate, checkAuth, updateUser]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip_code: user.zip_code || ""
      });
      // Check if user is admin from backend
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/check`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
      }
    } catch (error) {
      console.error("Admin status check failed:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/user/orders`, {
        withCredentials: true
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Orders fetch failed:", error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await axios.put(`${API}/auth/profile`, formData, {
        withCredentials: true
      });
      setUser(response.data);
      updateUser(response.data);
      setIsEditing(false);
      toast.success("Perfil actualizado");
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-agave border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bone">
      {/* Header */}
      <header className="bg-[#AFD29B] py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white hover:text-marigold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-fredoka">Volver a la tienda</span>
          </button>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Button 
                variant="ghost" 
                onClick={() => navigate("/admin")}
                className="text-white hover:text-marigold hover:bg-transparent"
                data-testid="admin-link"
              >
                <Settings className="w-5 h-5 mr-2" />
                Admin
              </Button>
            )}
            <img 
              src={user.picture || "https://via.placeholder.com/40"} 
              alt={user.name}
              className="w-10 h-10 rounded-full"
            />
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-white hover:text-red-200 hover:bg-transparent"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-gluten text-3xl md:text-4xl text-agave mb-8">
            ¡Hola, {user.name?.split(" ")[0]}!
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <Card className="lg:col-span-1">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 font-fredoka">
                  <User className="w-5 h-5 text-agave" />
                  Mi Perfil
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                >
                  {isEditing ? (
                    <><Save className="w-4 h-4 mr-1" /> Guardar</>
                  ) : (
                    <><Edit2 className="w-4 h-4 mr-1" /> Editar</>
                  )}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={user.picture || "https://via.placeholder.com/80"} 
                    alt={user.name}
                    className="w-20 h-20 rounded-full"
                  />
                  <div>
                    <p className="font-fredoka font-bold text-lg">{user.name}</p>
                    <p className="text-muted-foreground text-sm">{user.email}</p>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Nombre</Label>
                      <Input 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Teléfono</Label>
                      <Input 
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="10 dígitos"
                      />
                    </div>
                    <div>
                      <Label>Dirección</Label>
                      <Input 
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        placeholder="Calle y número"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Ciudad</Label>
                        <Input 
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Estado</Label>
                        <Input 
                          value={formData.state}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Código Postal</Label>
                      <Input 
                        value={formData.zip_code}
                        onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                        placeholder="5 dígitos"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {user.phone && (
                      <p className="text-sm"><span className="text-muted-foreground">Teléfono:</span> {user.phone}</p>
                    )}
                    {user.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-agave mt-0.5" />
                        <div className="text-sm">
                          <p>{user.address}</p>
                          {user.city && <p>{user.city}, {user.state}</p>}
                          {user.zip_code && <p>CP {user.zip_code}</p>}
                        </div>
                      </div>
                    )}
                    {!user.phone && !user.address && (
                      <p className="text-muted-foreground text-sm italic">
                        Agrega tu información de contacto y envío
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Orders Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-fredoka">
                  <Package className="w-5 h-5 text-agave" />
                  Mis Pedidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">Aún no tienes pedidos</p>
                    <Button 
                      onClick={() => navigate("/")}
                      className="bg-mexican hover:bg-mexican/90 text-white rounded-full"
                    >
                      ¡Hacer mi primer pedido!
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.id}
                        className="flex items-center justify-between p-4 bg-bone rounded-xl"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-agave/10 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-agave" />
                          </div>
                          <div>
                            <p className="font-fredoka font-bold">{order.product_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-fredoka font-bold text-agave">
                            ${order.amount.toLocaleString()} MXN
                          </p>
                          <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                            {order.payment_status === "paid" ? "Pagado" : order.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
